<template>
    <div
        v-bind:id="domId"
        v-bind:class="elementClasses"
        v-bind:data-type="element.ClassName"
        v-bind:data-name="name"
        v-bind:data-elementid="element.PublicId">
        <template v-if="elementComponent">
            <component
                v-bind:is="elementComponent"
                v-bind:element="element"
                v-bind:is-in-form-editor="isInFormEditor"
                v-on:help-clicked="toggleHelp">
                <template v-slot:help>
                    <!--
                        Need the custom class with margin-top style because the slide transition
                        is not smooth if we use bootstrap mt-* classes since they use !important
                    -->
                    <pc-alert
                        v-model:shown="showHelp"
                        class="element-help-margin-top"
                        dismissible>
                        <div>
                            <!-- Helptext container preserves whitespace so it must appear inline with its tags -->
                            <!-- eslint-disable-next-line -->
                            <div class="helptext-container">{{ element.Properties.helptext }}</div>
                            <div>
                                <a
                                    v-if="element.Properties.helplink"
                                    v-bind:href="element.Properties.helplink"
                                    target="_blank">
                                    {{ moreHelpLinkText }}
                                </a>
                            </div>
                        </div>
                    </pc-alert>
                </template>
            </component>
        </template>
        <template v-else>
            <div>{{ element.ClassName }} {{ element.PublicId }}</div>
        </template>
    </div>
</template>

<script>
    import date from "./date.vue";
    import name from "./name.vue";
    import integer from "./integer.vue";
    import decimal from "./decimal.vue";
    import contact from "./contact.vue";
    import address from "./address.vue";
    import textbox from "./textbox.vue";
    import textarea from "./textarea.vue";
    import literal from "./literal.vue";
    import dropdown from "./dropdown.vue";
    import radioList from "./radioButtonList.vue";
    import checkbox from "./checkbox.vue";
    import price from "./price.vue";
    import email from "./email.vue";
    import calcField from "./calculatedField.vue";
    import hyperlink from "./hyperlink.vue";
    import adminSelector from "./adminSelector.vue";
    import pvsystem from "./pvsystem.vue";
    import energystoragesystem from "./energystoragesystem.vue";
    import incentiveSelector from "./incentiveSelector.vue";
    import documentGeneration from "./documentGeneration.vue";
    import attachment from "./attachment.vue";
    import signatureRequest from "./signatureRequest.vue";
    import sensitiveData from "./sensitiveData/sensitiveData.vue";
    import webAdapterCall from "./webAdapterCall.vue";
    import customList from "./customList.vue";
    import summation from "./summation.vue";
    import table from "./tableElement.vue";
    import image from "./image.vue";
    import pageBreak from "./pageBreak.vue";
    import envelope from "./envelope/envelopeElement.vue";
    import projectTable from "./projectTableElement.vue";
    import rating from "./rating.vue";
    import location from "./location.vue";

    import { PROGRAM_TYPES, PROGRAM_NAMESPACE } from "SharedStores/Program/programStore";
    import { EDIT_FORM_NAMESPACE } from "Forms/EditForm/editFormStoreDefinitions";
    import { isModuleRegistered } from "SharedStores/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/element.i18n";

    const elementDictionary = {
        IntegerElement: integer,
        DecimalElement: decimal,
        PriceElement: price,
        DateElement: date,
        NameElement: name,
        AddressElement: address,
        ContactElement: contact,
        TextBoxElement: textbox,
        TextAreaElement: textarea,
        LiteralElement: literal,
        DropDownListElement: dropdown,
        RadioButtonListElement: radioList,
        CheckBoxElement: checkbox,
        EmailElement: email,
        FormulaResultElement: calcField,
        HyperlinkBuilderElement: hyperlink,
        AdminSelectorElement: adminSelector,
        PVSystemElement: pvsystem,
        EnergyStorageSystemElement: energystoragesystem,
        IncentiveSelectorElement: incentiveSelector,
        DocumentGenerationElement: documentGeneration,
        AttachmentElement: attachment,
        SignatureRequestElement: signatureRequest,
        SensitiveDataElement: sensitiveData,
        WebAdapterElement: webAdapterCall,
        ExternalDataSelectorElement: customList,
        SummationElement: summation,
        TableElement: table,
        ImageElement: image,
        PageBreakElement: pageBreak,
        EnvelopeElement: envelope,
        ProjectTableElement: projectTable,
        RatingElement: rating,
        LocationElement: location
    };

    export default {
        props: {
            element: { type: Object, required: true },
            isInFormEditor: { type: Boolean, required: false }
        },
        data() {
            return { showHelp: false };
        },
        computed: {
            elementComponent() {
                return elementDictionary[this.element.ClassName] || null;
            },
            isPvSystem() {
                return this.element.ClassName == "PVSystemElement";
            },
            domId() {
                return `element${this.element.PublicId}`;
            },
            hasHelp() {
                return Boolean(this.element.Properties.helptext || this.element.Properties.helplink);
            },
            elementClasses() {
                return "p-2" + (this.isPvSystem ? "" : " avoid-page-break-inside");
            },
            name() {
                if (
                    !isModuleRegistered(PROGRAM_NAMESPACE, this.$store.state) ||
                    isModuleRegistered(EDIT_FORM_NAMESPACE, this.$store.state)
                ) {
                    return this.element.ClassName;
                }
                let definition = null;
                if (this.element.ClassName === "AttachmentElement") {
                    definition = this.$store.getters[PROGRAM_TYPES.getters.attachmentDefinitionFromId](
                        this.element.AttachmentId
                    );
                } else if (this.element.EnvelopeDefinitionId) {
                    definition = this.$store.getters[PROGRAM_TYPES.getters.envelopeDefinitionFromId](
                        this.element.EnvelopeDefinitionId
                    );
                } else if (this.element.DataFieldGroupId) {
                    definition = this.$store.getters[PROGRAM_TYPES.getters.dataFieldGroupDefinitionFromId](
                        this.element.DataFieldGroupId
                    );
                } else {
                    definition = this.$store.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](
                        this.element.DataFieldId
                    );
                }

                return (definition && definition.Name) || this.element.ClassName;
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            toggleHelp() {
                this.showHelp = !this.showHelp;
            }
        }
    };
</script>
<style lang="scss" scoped>
    .element-help-margin-top {
        margin-top: 0.25rem;
    }

    .helptext-container {
        white-space: pre-line;
    }
</style>
